import React, { useEffect, useState } from "react";
import Logo from "../../../website/assets/images/common/logo_no_image.png";
import { useLoader } from "../../../base/context/LoaderProvider";
import { ToastTypes, useMessage } from "../../../base/context/MessageProvider";
import { useNavigate, useSearchParams } from "react-router-dom";
import { productListApiService } from "../../../service/admin/productApiService";
import { MdModeEdit, MdArrowBack, MdContentCopy } from "react-icons/md";
import { FaBox, FaRuler, FaWeight, FaTag, FaMapMarkerAlt, FaBarcode } from "react-icons/fa";
import { normalizeUrl, handleImgError } from "../../../utils/imageUtils";
import "./style.css";

const AdminProductDetails = () => {
  const [product, setProduct] = useState({});
  const [activeImage, setActiveImage] = useState("");
  const [variations, setVariations] = useState([]);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const loader = useLoader();
  const message = useMessage();

  const handleProduct = () => {
    loader.showInlineLoader("Loading...", "medium");
    productListApiService
      .getVariationProductById(searchParams.get("id"))
      .then((res) => {
        loader.hideLoader();
        if (res.status.code === 200) {
          setProduct(res.data);
          // Use productVariation if available, otherwise use variations from product
          setVariations(res.data.productVariation || res.data.variations || []);
          setActiveImage(res.data.imgUrl ? res.data.imgUrl[0] : "");
        } else if (res.status.code === 402) {
          navigate("/login");
        } else {
          message.showToast(res.error.message, ToastTypes.ERROR);
        }
      })
      .catch((err) => {
        message.showToast(err?.message || "Server error", ToastTypes.ERROR);
        loader.hideLoader();
      });
  };

  const handleEdit = (id) => navigate(`/admin/products/edit/${id}`);
  
  const handleProductVariationData = (sku) => {
    loader.showLoading(true);
    productListApiService
      .getProductBySku(sku)
      .then((res) => {
        loader.showLoading(false);
        if (res.status.code === 200) {
          setProduct(res.data);
          setActiveImage(res.data.imgUrl ? res.data.imgUrl[0] : "");
        } else {
          message.showToast(res.error.message, ToastTypes.ERROR);
        }
      })
      .catch((err) => {
        message.showToast(err?.message || "Server error", ToastTypes.ERROR);
        loader.showLoading(false);
      });
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    message.showToast("Copied!", ToastTypes.SUCCESS);
  };

  const formatPrice = (value, symbol = "₹") => {
    if (!value) return "-";
    const num = parseFloat(value);
    return `${symbol}${num.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // Extract images from description HTML
  const extractDescriptionImages = (html) => {
    if (!html) return { images: [], text: "" };
    const imgRegex = /<img[^>]+src=["']([^"']+)["'][^>]*>/gi;
    const images = [];
    let match;
    while ((match = imgRegex.exec(html)) !== null) {
      images.push(match[1]);
    }
    // Remove img tags to get text only
    const text = html.replace(/<img[^>]*>/gi, "").replace(/<figure[^>]*>.*?<\/figure>/gi, "");
    return { images, text };
  };

  const descriptionData = extractDescriptionImages(product.description);

  useEffect(() => { handleProduct(); }, []);

  return (
    <div className="apd-page">
      {/* Header */}
      <div className="apd-header">
        <div className="apd-header-left">
          <button className="apd-back-btn" onClick={() => navigate(-1)}><MdArrowBack /></button>
          <div className="apd-header-title">
            <span className="apd-header-text">Product Details</span>
            {product.offerId && <span className="apd-header-offerid">#{product.offerId}</span>}
          </div>
        </div>
        <button className="apd-edit-btn" onClick={() => handleEdit(product._id)}>
          <MdModeEdit /> Edit Product
        </button>
      </div>

      {/* Main Content */}
      <div className="apd-content">
        {/* Left: Images */}
        <div className="apd-images-section">
          <div className="apd-main-image">
            {activeImage ? (
              <img src={normalizeUrl(activeImage)} alt="Product" onError={handleImgError} />
            ) : (
              <img src={Logo} alt="No Image" />
            )}
          </div>
          <div className="apd-thumbnails">
            {product.imgUrl?.map((url, index) => (
              <div key={index} className={`apd-thumb ${activeImage === url ? "apd-thumb-active" : ""}`} onClick={() => setActiveImage(url)}>
                <img src={normalizeUrl(url)} alt="" onError={handleImgError} />
              </div>
            ))}
          </div>
        </div>

        {/* Right: Details */}
        <div className="apd-details-section">
          <h1 className="apd-title">{product.title || "Product Title"}</h1>
          
          <div className="apd-sku-row">
            <span className="apd-sku">SKU: {product.sku}</span>
            <MdContentCopy className="apd-copy-icon" onClick={() => copyToClipboard(product.sku)} />
            <span className={`apd-status ${product.status === "5" ? "apd-status-active" : "apd-status-pending"}`}>
              {product.status === "5" ? "Active" : "Pending"}
            </span>
          </div>

          {/* Price Row */}
          <div className="apd-price-row">
            <div className="apd-price-item">
              <span className="apd-price-label">VENDOR PRICE (RMB)</span>
              <span className="apd-price-value apd-price-vendor">¥{product.vendor_price || 0}</span>
            </div>
            <div className="apd-price-item">
              <span className="apd-price-label">SELLING PRICE</span>
              <span className="apd-price-value apd-price-selling">{formatPrice(product.sp)}</span>
            </div>
            <div className="apd-price-item">
              <span className="apd-price-label">MRP</span>
              <span className="apd-price-value">{formatPrice(product.mrp)}</span>
            </div>
          </div>

          {/* Info Grid */}
          <div className="apd-info-grid">
            <div className="apd-info-item">
              <FaBarcode className="apd-info-icon" />
              <div><span className="apd-info-label">VENDOR ID</span><span className="apd-info-value">{product.vendor_id || "-"}</span></div>
            </div>
            <div className="apd-info-item">
              <FaTag className="apd-info-icon" />
              <div><span className="apd-info-label">CATEGORY</span><span className="apd-info-value">{product.c_name || "-"}</span></div>
            </div>
            <div className="apd-info-item">
              <FaBox className="apd-info-icon" />
              <div><span className="apd-info-label">BRAND</span><span className="apd-info-value">{product.brand || "Generic"}</span></div>
            </div>
            <div className="apd-info-item">
              <FaMapMarkerAlt className="apd-info-icon" />
              <div><span className="apd-info-label">LOCATION</span><span className="apd-info-value">{product.location || "-"}</span></div>
            </div>
          </div>

          {/* Specifications */}
          <div className="apd-specs">
            <h3 className="apd-section-title">Specifications</h3>
            <div className="apd-specs-grid">
              <div className="apd-spec-item">
                <FaRuler className="apd-spec-icon" />
                <div><span className="apd-spec-label">DIMENSIONS (L×W×H)</span><span className="apd-spec-value">{product.length || 0} × {product.width || 0} × {product.height || 0} cm</span></div>
              </div>
              <div className="apd-spec-item">
                <FaWeight className="apd-spec-icon" />
                <div><span className="apd-spec-label">WEIGHT</span><span className="apd-spec-value">{product.weight || 0} kg</span></div>
              </div>
              <div className="apd-spec-item">
                <FaBox className="apd-spec-icon" />
                <div><span className="apd-spec-label">PACK UNIT</span><span className="apd-spec-value">{product.pack_unit || "-"}</span></div>
              </div>
              <div className="apd-spec-item">
                <FaTag className="apd-spec-icon" />
                <div><span className="apd-spec-label">HSN CODE</span><span className="apd-spec-value">{product.hsn || "-"}</span></div>
              </div>
              <div className="apd-spec-item">
                <FaBox className="apd-spec-icon" />
                <div><span className="apd-spec-label">QUANTITY</span><span className="apd-spec-value">{product.quantity || 0}</span></div>
              </div>
              <div className="apd-spec-item">
                <FaBarcode className="apd-spec-icon" />
                <div><span className="apd-spec-label">NODE ID</span><span className="apd-spec-value">{product.nodeId || "-"}</span></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Description */}
      {product.description && (
        <div className="apd-description">
          <h3 className="apd-section-title">Description</h3>
          {descriptionData.text && (
            <div className="apd-description-text" dangerouslySetInnerHTML={{ __html: descriptionData.text }} />
          )}
          {descriptionData.images.length > 0 && (
            <div className="apd-description-images">
              {descriptionData.images.map((img, index) => (
                <div key={index} className="apd-desc-thumb" onClick={() => setActiveImage(img)}>
                  <img src={normalizeUrl(img)} alt="" onError={handleImgError} />
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Variations */}
      {variations && variations.length > 0 && (
        <div className="apd-variations">
          <h3 className="apd-section-title">Variations ({variations.length})</h3>
          <div className="apd-variations-grid">
            {variations.map((item, index) => {
              // Handle both productVariation format and raw variations format
              const imgUrl = item?.imgUrl?.[0] || item?.skuImageUrl || null;
              const sku = item.sku || item.skuId || `VAR-${index + 1}`;
              const price = item.sp || item.price || item.consignPrice || 0;
              const qty = item.quantity || item.amountOnSale || 0;
              // Get attributes from skuAttributes array
              const attrs = item.skuAttributes || [];
              const colorAttr = attrs.find(a => a.attributeName?.toLowerCase().includes('color') || a.attributeNameTrans?.toLowerCase().includes('color'));
              const sizeAttr = attrs.find(a => a.attributeName?.toLowerCase().includes('size') || a.attributeNameTrans?.toLowerCase().includes('size'));
              const color = item.color_map || colorAttr?.valueTrans || colorAttr?.value || null;
              const size = item.size_map || sizeAttr?.valueTrans || sizeAttr?.value || null;
              
              return (
                <div key={index} className={`apd-variation-card ${item.is_delete ? "apd-variation-deleted" : ""}`} onClick={() => item.sku && handleProductVariationData(item.sku)}>
                  <div className="apd-variation-image">
                    {imgUrl ? (
                      <img src={normalizeUrl(imgUrl)} alt="" onError={handleImgError} />
                    ) : (
                      <div className="apd-variation-no-img">-</div>
                    )}
                  </div>
                  <div className="apd-variation-info">
                    <span className="apd-variation-sku">{sku}</span>
                    <div className="apd-variation-attrs">
                      {color && <span className="apd-variation-attr">{color}</span>}
                      {size && <span className="apd-variation-attr">{size}</span>}
                      {attrs.filter(a => !a.attributeName?.toLowerCase().includes('color') && !a.attributeName?.toLowerCase().includes('size')).map((attr, i) => (
                        <span key={i} className="apd-variation-attr">{attr.valueTrans || attr.value}</span>
                      ))}
                    </div>
                    <div className="apd-variation-bottom">
                      <span className="apd-variation-price">{formatPrice(price)}</span>
                      <span className="apd-variation-qty">Qty: {qty}</span>
                    </div>
                  </div>
                  {item._id && (
                    <button className="apd-variation-edit" onClick={(e) => { e.stopPropagation(); handleEdit(item._id); }}><MdModeEdit /></button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminProductDetails;
